import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTrackModal } from '@/features/checklist-inicio/store/useModalTrackerStore';
import { ModalForm, FooterStatus, TextButton, Select } from '@/contasis';
import { usarAyudaGuiada, registrarTour, solicitarInicioTour, solicitarCierreTour } from '@/shared/tour';
import { tourNuevoProducto } from '@/features/tour/tourNuevoProducto';
import Tooltip from '@/contasis/components/Tooltip/Tooltip';
import { useToast } from '@/contexts/ContextoToast';
import CategoriaModal from '@/components/Configuracion/Categoria/CategoriaModal';
import { useAccountingAccounts } from '@/hooks/useCuentasContables';
import { AccountingAccountModal } from '@/components/Configuracion/Negocio/ModalCuentaContable';
import type { AccountingAccountInput } from '@/modelos/CuentaContable';
import { useFamilias } from '@/hooks/useFamilias';
import { useCentrosCosto } from '@/hooks/useCentrosCosto';
import { usePresupuestos } from '@/hooks/usePresupuestos';
import { ModalCatalogoCodigoNombre, type CatalogoCodigoNombreInput } from './ModalCatalogoCodigoNombre';
import { FieldsConfigPanel } from './PanelConfigCampos';
import { ProductTypeSelector } from './Formulario/SelectorTipo';
import {
  ProductBarcodeField,
  ProductCategoryField,
  ProductAliasField,
  ProductBrandField,
  ProductCodeField,
  ProductModelField,
  ProductNameField
} from './Formulario/SeccionBasicos';
import { ProductPricingSection } from './Formulario/SeccionPrecios';
import { ProductMinimumUnitField, ProductUnitFamilyField } from './Formulario/SeccionUnidades';
import { ProductAdditionalUnitsTable } from './Formulario/TablaUnidadesAdicionales';
import { ProductAvailabilitySection } from './Formulario/SeccionDisponibilidad';
import { ProductDescriptionField, ProductWeightField } from './Formulario/SeccionDescripcion';
import { ProductImageUpload } from './Formulario/CargaImagen';
import {
  ProductDiscountField,
  ProductProfitPercentField,
  ProductPurchasePriceField
} from './Formulario/SeccionFinanciera';
import {
  ProductExistenceTypeField,
  ProductFactoryCodeField,
  ProductSunatCodeField
} from './Formulario/SeccionCodigos';
import { SeccionInformacionTributariaProducto } from './SeccionInformacionTributariaProducto';

import type { ProductInput } from '@/hooks/productos/useProductoStore';
import { useProductFieldsConfig } from '@/hooks/productos/useProductoFieldsConfig';
import { useProductForm } from '@/hooks/productos/useProductoFormulario';
import type { Product } from '@/modelos/productos/Producto';
import { useConfigurationContext } from '@/contexts/ContextoConfiguracion';
import type { Category } from '@/modelos/productos/Producto';
import { useWorkspace } from '@/contexts/ContextoWorkspace';
import { unidadMedidaService } from '@/services/api/unidadMedidas.service';
import { afectacionTributariaService } from '@/services/api/afectacionTributarias.service';
import { categoriaService } from '@/services/api/categorias.service';
import { establecimientosService } from '@/services/api/establecimientos.service';
import type { AfectacionTributaria as AfectacionAPI, Categoria as CategoriaAPI } from '@/modelos/Configuracion';
import { mapearCategoriaDesdeBackend } from '@/modelos/Unidad';
import type { Unit } from '@/modelos/Unidad';
import type { Establecimiento } from '@/modelos/Establecimiento';

function formatFieldList(fields: string[]): string {
  if (fields.length <= 1) return fields[0] ?? '';
  return `${fields.slice(0, -1).join(', ')} y ${fields[fields.length - 1]}`;
}

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (productData: ProductInput) => Product | Promise<Product | void> | void;
  product?: Product;
  prefillName?: string;
  categories: Category[];
  allProducts: Product[];
}

const ProductModal: React.FC<ProductModalProps> = ({
  isOpen,
  onClose,
  onSave,
  product,
  prefillName,
  categories,
  allProducts
}) => {
  const { state: configState, dispatch: configDispatch } = useConfigurationContext();
  const { empresaId, establecimientoId: workspaceEstablecimientoId } = useWorkspace();
  // Mientras está abierto, el checklist de inicio se retira (id único por instancia).
  useTrackModal(`producto-${useId()}`, isOpen);

  const [localEstablecimientos, setLocalEstablecimientos] = useState<Establecimiento[]>([]);

  const Establecimientos = useMemo(() => {
    const source = localEstablecimientos.length > 0 ? localEstablecimientos : configState.Establecimientos;
    return source.filter(est => est.estaActivoEstablecimiento !== false && est.esActivo !== false);
  }, [localEstablecimientos, configState.Establecimientos]);

  const defaultEstablecimientoId = useMemo(() => {
    if (workspaceEstablecimientoId && Establecimientos.some(est => est.id === workspaceEstablecimientoId)) {
      return workspaceEstablecimientoId;
    }
    const main = Establecimientos.find(est => est.isMainEstablecimiento ?? est.esPrincipal);
    return (main?.id ?? Establecimientos[0]?.id ?? '');
  }, [workspaceEstablecimientoId, Establecimientos]);

  // Estado local para catálogos cargados desde el API
  const [localUnits, setLocalUnits] = useState<Unit[]>([]);
  const [localAfectaciones, setLocalAfectaciones] = useState<AfectacionAPI[]>([]);
  const [localCategorias, setLocalCategorias] = useState<CategoriaAPI[]>([]);

  // Cargar unidades, impuestos y categorías desde el API al abrir el modal
  useEffect(() => {
    if (!isOpen) {
      setLocalUnits([]);
      setLocalAfectaciones([]);
      setLocalCategorias([]);
      setLocalEstablecimientos([]);
      return;
    }
    if (!empresaId) return;

    unidadMedidaService.getAll(empresaId)
      .then(res => {
        if (res.ok && res.data) {
          const mapped: Unit[] = (res.data as unknown as import('@/modelos/Configuracion').UnidadMedida[])
            .filter(u => u.esVisible)
            .map(u => ({
              id: u.id,
              code: u.codigoSunat,
              name: u.nombre,
              symbol: u.simbolo ?? u.codigoSunat,
              description: u.descripcion ?? '',
              category: mapearCategoriaDesdeBackend(u.categoriaCodigo),
              conversionFactor: u.factorConversion,
              decimalPlaces: u.decimales,
              isActive: u.esVisible,
              isSystem: u.esSistema,
              isFavorite: u.esFavorito,
              isVisible: u.esVisible,
              displayOrder: u.ordenVisualizacion,
              usageCount: 0,
              createdAt: new Date(u.createdAt),
              updatedAt: new Date(u.updatedAt),
            }));
          setLocalUnits(mapped);
        }
      })
      .catch(() => { /* fallback a configState.units */ });

    afectacionTributariaService.getAll(empresaId)
      .then(res => {
        if (res.ok && res.data) {
          setLocalAfectaciones((res.data as AfectacionAPI[]).filter(a => a.esVisible));
        }
      })
      .catch(() => { /* fallback a configState.taxes */ });

    categoriaService.getAll(empresaId)
      .then(res => {
        if (res.ok && res.data) {
          setLocalCategorias(res.data as CategoriaAPI[]);
        }
      })
      .catch(() => { /* fallback a prop categories */ });

    establecimientosService.getAll()
      .then(res => {
        if (res.ok && res.data) {
          const items = Array.isArray(res.data) ? res.data : res.data.data ?? [];
          setLocalEstablecimientos(items);
        }
      })
      .catch(() => { /* fallback a configState.Establecimientos */ });
  }, [isOpen, empresaId]);

  const availableUnits = useMemo(
    () => localUnits.length > 0 ? localUnits : configState.units,
    [localUnits, configState.units]
  );

  const taxOptions = useMemo(() => {
    if (localAfectaciones.length > 0) {
      const sorted = [...localAfectaciones].sort((a, b) => {
        if (a.esDefecto && !b.esDefecto) return -1;
        if (!a.esDefecto && b.esDefecto) return 1;
        return b.impuesto - a.impuesto;
      });
      return sorted.map(a => {
        const nombre = a.alias || a.nombre;
        const cleanName = nombre.replace(/^IGV\s*\d*%?\s*/i, 'IGV').trim();
        const displayName = cleanName.toLowerCase().startsWith('igv') ? 'IGV' : cleanName;
        const label = `${displayName} (${a.impuesto.toFixed(2)}%)`;
        return { id: a.id, code: a.codigo, value: label, label, codigoTributo: a.codigoTributo, impuesto: a.impuesto, nombre: a.nombre };
      });
    }
    const taxes = configState.taxes ?? [];
    const visible = taxes.filter(tax => tax.isActive);
    const sorted = [...visible].sort((a, b) => {
      if ((a as any).isDefault && !(b as any).isDefault) return -1;
      if (!(a as any).isDefault && (b as any).isDefault) return 1;
      const rateA = a.rate;
      const rateB = b.rate;
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA.includes('igv') && Math.abs(rateA - 18) < 0.01) return -1;
      if (nameB.includes('igv') && Math.abs(rateB - 18) < 0.01) return 1;
      if (nameA.includes('igv') && Math.abs(rateA - 10) < 0.01) return -1;
      if (nameB.includes('igv') && Math.abs(rateB - 10) < 0.01) return 1;
      if (nameA.includes('exo') || nameA.includes('exoner')) return -1;
      if (nameB.includes('exo') || nameB.includes('exoner')) return 1;
      if (nameA.includes('ina') || nameA.includes('inafec')) return -1;
      if (nameB.includes('ina') || nameB.includes('inafec')) return 1;
      if (nameA.includes('exp') || nameA.includes('export')) return -1;
      if (nameB.includes('exp') || nameB.includes('export')) return 1;
      return rateB - rateA;
    });
    const formatLabel = (rate: number, name: string): string => {
      const rateFixed = rate.toFixed(2);
      const cleanName = name.replace(/^IGV\s*\d*%?\s*/i, 'IGV').trim();
      if (cleanName.toLowerCase().startsWith('igv')) return `IGV (${rateFixed}%)`;
      return `${cleanName} (${rateFixed}%)`;
    };
    return sorted.map(tax => ({
      id: tax.id,
      code: tax.code,
      value: formatLabel(tax.rate, tax.name),
      label: formatLabel(tax.rate, tax.name),
      impuesto: tax.rate,
      nombre: tax.name,
    }));
  }, [localAfectaciones, configState.taxes]);

  const defaultTaxLabel = useMemo(() => {
    if (localAfectaciones.length === 0) return undefined;
    const def = localAfectaciones.find(a => a.esDefecto) ?? localAfectaciones[0];
    if (!def) return undefined;
    const nombre = def.alias || def.nombre;
    const cleanName = nombre.replace(/^IGV\s*\d*%?\s*/i, 'IGV').trim();
    const displayName = cleanName.toLowerCase().startsWith('igv') ? 'IGV' : cleanName;
    return `${displayName} (${def.impuesto.toFixed(2)}%)`;
  }, [localAfectaciones]);

  // Categorías efectivas: API primero, luego prop
  const effectiveCategories = useMemo((): Category[] => {
    if (localCategorias.length > 0) {
      return localCategorias.map(c => ({
        id: c.id,
        nombre: c.nombre,
        descripcion: c.descripcion,
        color: c.color,
        productCount: 0,
        fechaCreacion: new Date(c.createdAt),
      }));
    }
    return categories;
  }, [localCategorias, categories]);

  const { showSuccess, showError } = useToast();
  const [crearOtro, setCrearOtro] = useState(false);

  const { accounts: cuentasContables, createAccount: crearCuentaContable, refresh: refrescarCuentasContables } = useAccountingAccounts();
  const [mostrarModalContable, setMostrarModalContable] = useState(false);

  const onSubmitGuardarModalContable = async (input: AccountingAccountInput) => {
    try {
      const created = await crearCuentaContable(input);
      await refrescarCuentasContables();
      setFormData(prev => ({
        ...prev,
        cuentaContableId: created.id,
        cuentaContableCodigo: created.code
      }));
      setMostrarModalContable(false);
      showSuccess('Cuenta contable creada correctamente');
    } catch {
      showError('Error al crear la cuenta contable');
    }
  };

  const { familias, crearFamilia } = useFamilias();
  const [mostrarModalFamilia, setMostrarModalFamilia] = useState(false);

  const onSubmitGuardarModalFamilia = async (input: CatalogoCodigoNombreInput) => {
    try {
      const created = await crearFamilia(input);
      setFormData(prev => ({
        ...prev,
        familiaId: created.id,
        familiaNombre: created.nombre
      }));
      setMostrarModalFamilia(false);
      showSuccess('Familia creada correctamente');
    } catch {
      showError('Error al crear la familia');
    }
  };

  const { centrosCosto, crearCentroCosto } = useCentrosCosto();
  const [mostrarModalCentroCosto, setMostrarModalCentroCosto] = useState(false);

  const onSubmitGuardarModalCentroCosto = async (input: CatalogoCodigoNombreInput) => {
    try {
      const created = await crearCentroCosto(input);
      setFormData(prev => ({
        ...prev,
        centroCostoId: created.id,
        centroCostoNombre: created.nombre
      }));
      setMostrarModalCentroCosto(false);
      showSuccess('Centro de costo creado correctamente');
    } catch {
      showError('Error al crear el centro de costo');
    }
  };

  const { presupuestos, crearPresupuesto } = usePresupuestos();
  const [mostrarModalPresupuesto, setMostrarModalPresupuesto] = useState(false);

  const onSubmitGuardarModalPresupuesto = async (input: CatalogoCodigoNombreInput) => {
    try {
      const created = await crearPresupuesto(input);
      setFormData(prev => ({
        ...prev,
        presupuestoId: created.id,
        presupuestoNombre: created.nombre
      }));
      setMostrarModalPresupuesto(false);
      showSuccess('Presupuesto creado correctamente');
    } catch {
      showError('Error al crear el presupuesto');
    }
  };

  const {
    fieldsConfig,
    isPanelOpen,
    setIsPanelOpen,
    applyFieldsConfig,
    resetToDefault,
    isFieldVisible,
    isFieldRequired
  } = useProductFieldsConfig();

  const {
    formData,
    setFormData,
    errors,
    additionalUnitErrors,
    loading,
    imagePreview,
    archivosExistentes,
    productType,
    handleProductTypeChange,
    selectedUnitFamily,
    unitFamilies,
    isDescriptionExpanded,
    setIsDescriptionExpanded,
    unitInfoMessage,
    setUnitInfoMessage,
    showCategoryModal,
    setShowCategoryModal,
    baseUnitOptions,
    isUsingFallbackUnits,
    remainingUnitsForAdditional,
    findUnitByCode,
    formatFactorValue,
    handleUnitFamilyChange,
    handleBaseUnitChange,
    addAdditionalUnit,
    removeAdditionalUnit,
    updateAdditionalUnit,
    getAdditionalUnitOptions,
    handleSubmit,
    handleImageUpload,
    handleFilesChange,
    initializeFormForNewProduct,
  } = useProductForm({
    isOpen,
    product,
    categories: effectiveCategories,
    availableUnits,
    allProducts,
    isFieldVisible,
    isFieldRequired,
    onSave,
    onClose,
    defaultTaxLabel,
    activeEstablecimientos: Establecimientos,
    defaultEstablecimientoId,
    prefillName
  });

  useEffect(() => {
    if (taxOptions.length === 0) return;
    setFormData(prev => {
      if (prev.afectacionTributariaId) {
        const matched = taxOptions.find(o => o.id === prev.afectacionTributariaId);
        if (matched) {
          if (prev.impuesto === matched.value) return prev;
          return { ...prev, impuesto: matched.value };
        }
      }
      const found = prev.impuesto ? taxOptions.find(o => o.value === prev.impuesto) : null;
      if (found && found.id !== 'legacy-impuesto') {
        return {
          ...prev,
          afectacionTributariaId: found.id,
          afectacionTributariaCodigo: found.code,
          afectacionTributariaCodigoTributo: (found as any).codigoTributo,
          afectacionTributariaImpuesto: found.impuesto !== undefined ? String(found.impuesto) : prev.afectacionTributariaImpuesto,
          afectacionTributariaNombre: found.nombre,
        };
      }
      const opt = taxOptions[0];
      return {
        ...prev,
        impuesto: opt.value,
        afectacionTributariaId: opt.id,
        afectacionTributariaCodigo: opt.code,
        afectacionTributariaCodigoTributo: (opt as any).codigoTributo,
        afectacionTributariaImpuesto: opt.impuesto !== undefined ? String(opt.impuesto) : prev.afectacionTributariaImpuesto,
        afectacionTributariaNombre: opt.nombre,
      };
    });
  }, [taxOptions]);

  const showBarcode = isFieldVisible('codigoBarras');
  const showTax = isFieldVisible('impuesto');
  const showCategory = isFieldVisible('categoria');
  const showAlias = isFieldVisible('alias');
  const showSunat = isFieldVisible('codigoSunat');
  const showPresentacionesComerciales = isFieldVisible('presentacionesComerciales');
  const showFamilia = isFieldVisible('familia');
  const showCentroCosto = isFieldVisible('centroCosto');
  const showPresupuesto = isFieldVisible('presupuesto');

  useEffect(() => {
    if (showPresentacionesComerciales && formData.unidadesMedidaAdicionales.length === 0) {
      addAdditionalUnit();
    }
  }, [showPresentacionesComerciales, addAdditionalUnit]);

  const showAvailability = Establecimientos.length > 1;

  type FieldId =
    | 'nombre'
    | 'codigo'
    | 'codigoBarras'
    | 'impuesto'
    | 'establecimiento'
    | 'unidad'
    | 'categoria'
    | 'alias'
    | 'codigoSunat'
    | 'imagen'
    | 'presentacionesComerciales'
    | 'descripcion'
    | 'peso'
    | 'marca'
    | 'modelo'
    | 'codigoFabrica'
    | 'tipoExistencia'
    | 'descuentoProducto'
    | 'porcentajeGanancia'
    | 'precioCompra'
    | 'tipoProducto';

  const [touchedFields, setTouchedFields] = useState<Partial<Record<FieldId, boolean>>>({});
  const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);

  const markTouched = useCallback((fieldId: FieldId) => {
    setTouchedFields(prev => (prev[fieldId] ? prev : { ...prev, [fieldId]: true }));
  }, []);

  const isFilled = useCallback((fieldId: FieldId): boolean => {
    switch (fieldId) {
      case 'nombre':
        return formData.nombre.trim().length > 0;
      case 'codigo':
        return formData.codigo.trim().length > 0;
      case 'categoria':
        return (formData.categoria ?? '').trim().length > 0;
      case 'codigoBarras':
        return (formData.codigoBarras ?? '').trim().length > 0;
      case 'alias':
        return (formData.alias ?? '').trim().length > 0;
      case 'marca':
        return (formData.marca ?? '').trim().length > 0;
      case 'modelo':
        return (formData.modelo ?? '').trim().length > 0;
      case 'descripcion':
        return (formData.descripcion ?? '').trim().length > 0;
      case 'codigoSunat':
        return (formData.codigoSunat ?? '').trim().length > 0;
      case 'codigoFabrica':
        return (formData.codigoFabrica ?? '').trim().length > 0;
      case 'tipoExistencia':
        return Boolean(formData.tipoExistencia);
      case 'impuesto':
        return (formData.impuesto ?? '').trim().length > 0;
      case 'unidad':
        return Boolean(formData.unidad);
      case 'establecimiento':
        return (formData.establecimientoIds ?? []).length > 0;
      case 'imagen':
        return Boolean(imagePreview);
      case 'precioCompra':
        return formData.precioCompra !== undefined && formData.precioCompra !== null;
      case 'porcentajeGanancia':
        return formData.porcentajeGanancia !== undefined && formData.porcentajeGanancia !== null;
      case 'descuentoProducto':
        return formData.descuentoProducto !== undefined && formData.descuentoProducto !== null;
      case 'peso':
        return formData.peso !== undefined && formData.peso !== null;
      case 'presentacionesComerciales':
        return (formData.unidadesMedidaAdicionales ?? []).length > 0;
      case 'tipoProducto':
        return Boolean(productType);
      default:
        return false;
    }
  }, [formData, imagePreview, productType]);

  const shouldBlueHint = useCallback(
    (fieldId: FieldId) => (fieldId === 'nombre' || fieldId === 'codigo') && !isFilled(fieldId),
    [isFilled]
  );

  // Filtrar errores para mostrar solo los de campos touched o cuando hasSubmitAttempt
  const visibleErrors = useMemo(() => {
    if (hasSubmitAttempt) return errors;
    
    const filtered: typeof errors = {};
    (Object.keys(errors) as Array<keyof typeof errors>).forEach(key => {
      if (touchedFields[key as FieldId]) {
        filtered[key] = errors[key];
      }
    });
    return filtered;
  }, [errors, touchedFields, hasSubmitAttempt]);

  // Nombres legibles de los campos obligatorios que aún faltan por completar
  const missingFields = useMemo(() => {
    const missing: string[] = [];

    // Nombre siempre es requerido
    if (!formData.nombre.trim()) missing.push('nombre');

    // Código siempre es requerido
    if (!formData.codigo.trim()) missing.push('código');

    // Impuesto (si es visible)
    if (isFieldVisible('impuesto') && (!formData.impuesto || formData.impuesto.trim() === '')) {
      missing.push('impuesto');
    }

    // Unidad (si es visible)
    if (isFieldVisible('unidad') && (!formData.unidad || formData.unidad.trim() === '')) {
      missing.push('unidad de medida');
    }

    // Categoría (si es visible Y requerida)
    if (isFieldVisible('categoria') && isFieldRequired('categoria') && !formData.categoria) {
      missing.push('categoría');
    }

    // Descripción (si es visible Y requerida)
    if (isFieldVisible('descripcion') && isFieldRequired('descripcion') && !formData.descripcion?.trim()) {
      missing.push('descripción');
    }

    // Código de barras (si es visible Y requerido)
    if (isFieldVisible('codigoBarras') && isFieldRequired('codigoBarras')) {
      const normalized = (formData.codigoBarras || '').trim();
      if (!normalized) {
        missing.push('código de barras');
      }
    }

    return missing;
  }, [formData, isFieldVisible, isFieldRequired]);

  // Verificar si todos los campos obligatorios están completos
  const isFormValid = missingFields.length === 0;

  // Cantidad de campos faltantes para FooterStatus
  const missingFieldsCount = missingFields.length;

  const handleSubmitWithTouch = useCallback(
    async (event?: React.FormEvent | React.MouseEvent) => {
      setHasSubmitAttempt(true);
      const savedProduct = await handleSubmit(event);
      
      if (savedProduct) {
        // Mostrar toast con el nombre del producto creado
        const productName = savedProduct.nombre || 'Producto';
        showSuccess(`Producto "${productName}" ${product ? 'actualizado' : 'creado'} exitosamente`);
        
        if (crearOtro && !product) {
          // Resetear formulario para crear otro
          initializeFormForNewProduct();
          setHasSubmitAttempt(false);
        } else {
          // Cerrar modal
          onClose();
        }
      }
    },
    [handleSubmit, showSuccess, product, crearOtro, initializeFormForNewProduct, onClose]
  );

  const { ayudaActivada, estaTourCompletado, estaTourOmitido, marcarTourCompletado } = usarAyudaGuiada();
  const tourLanzadoRef = useRef(false);
  const intentoTourRef = useRef<number | null>(null);
  const intentosRestantesRef = useRef(0);

  useEffect(() => {
    registrarTour(tourNuevoProducto);
  }, []);

  useEffect(() => {
    if (!isOpen && tourLanzadoRef.current) {
      if (!estaTourCompletado(tourNuevoProducto.id, tourNuevoProducto.version)) {
        marcarTourCompletado(tourNuevoProducto.id, tourNuevoProducto.version);
      }
      solicitarCierreTour();
      tourLanzadoRef.current = false;
    }
  }, [isOpen, estaTourCompletado, marcarTourCompletado]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!isOpen || product) return undefined;
    if (!ayudaActivada) return undefined;
    if (tourLanzadoRef.current) return undefined;
    if (
      estaTourCompletado(tourNuevoProducto.id, tourNuevoProducto.version) ||
      estaTourOmitido(tourNuevoProducto.id, tourNuevoProducto.version)
    ) {
      return undefined;
    }

    intentosRestantesRef.current = 8;

    const intentarIniciar = () => {
      if (!isOpen || product || tourLanzadoRef.current) return;
      const elementosListos = tourNuevoProducto.pasos.every((paso) =>
        Boolean(paso.selector && document.querySelector(paso.selector))
      );
      if (elementosListos) {
        solicitarInicioTour(tourNuevoProducto.id);
        tourLanzadoRef.current = true;
        return;
      }
      intentosRestantesRef.current -= 1;
      if (intentosRestantesRef.current <= 0) return;
      intentoTourRef.current = window.setTimeout(intentarIniciar, 120);
    };

    intentoTourRef.current = window.setTimeout(intentarIniciar, 60);

    return () => {
      if (intentoTourRef.current) {
        window.clearTimeout(intentoTourRef.current);
        intentoTourRef.current = null;
      }
    };
  }, [ayudaActivada, estaTourCompletado, estaTourOmitido, isOpen, product]);

  return (
    <>
      <ModalForm
        isOpen={isOpen}
        title={product ? 'Editar producto' : 'Nuevo producto / servicio'}
        onClose={onClose}
        onSubmit={handleSubmitWithTouch}
        submitLabel={product ? 'Actualizar' : (productType === 'SERVICIO' ? 'Crear servicio' : 'Crear bien')}
        isSubmitting={loading}
        canSubmit={isFormValid}
        size="large"
        showCreateAnother={!product}
        createAnotherLabel="Crear otro"
        footerTop={
          !product ? (
            <FooterStatus
              missingCount={missingFieldsCount}
              pendingText={`Faltan {count} campos (${formatFieldList(missingFields)}) para crear el ${productType === 'SERVICIO' ? 'servicio' : 'bien'}`}
              readyText={`Puedes crear el ${productType === 'SERVICIO' ? 'servicio' : 'bien'} ahora o completar los datos adicionales.`}
            />
          ) : undefined
        }
        onCreateAnotherChange={setCrearOtro}
        headerActions={
          <Tooltip text="Agrega u oculta campos del formulario." position="left">
            <TextButton
              intent="primary"
              size="sm"
              onClick={() => setIsPanelOpen(true)}
            >
              + Más campos
            </TextButton>
          </Tooltip>
        }
      >
        <div className="space-y-4">
          <div data-tour="producto-tipo" className="inline-block">
            <ProductTypeSelector
              productType={productType}
              onChange={(type) => {
                handleProductTypeChange(type);
                markTouched('tipoProducto');
              }}
            />
          </div>

          {/* A) DEFAULT / OBLIGATORIO */}
          <div className="flex flex-col lg:flex-row gap-12 items-start">
              <div className="w-full lg:flex-1 lg:max-w-[620px] space-y-4">
                {/* Nombre (obligatorio) */}
                <ProductNameField
                  formData={formData}
                  setFormData={setFormData}
                  errors={visibleErrors}
                  onBlur={() => markTouched('nombre')}
                  showBlueHint={shouldBlueHint('nombre')}
                />

                {/* Código + Código de barras (misma fila) */}
                <div data-tour="producto-identificacion" className={`grid grid-cols-1 gap-6 ${showBarcode ? 'lg:grid-cols-2' : ''}`}>
                  <ProductCodeField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    onBlur={() => markTouched('codigo')}
                    showBlueHint={shouldBlueHint('codigo')}
                  />
                  <ProductBarcodeField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('codigoBarras')}
                  />
                </div>

                {/* Impuesto + Disponibilidad (misma fila) */}
                <div
                  data-tour="producto-precio"
                  className={`grid grid-cols-1 gap-6 ${showTax && showAvailability ? 'lg:grid-cols-2' : ''}`}
                >
                  <ProductPricingSection
                    formData={formData}
                    setFormData={setFormData}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    taxOptions={taxOptions}
                    onBlur={() => markTouched('impuesto')}
                    onTaxChange={(option) => setFormData(prev => ({
                      ...prev,
                      afectacionTributariaId: option.id !== 'legacy-impuesto' ? option.id : prev.afectacionTributariaId,
                      afectacionTributariaCodigo: option.code !== 'LEGACY' ? option.code : prev.afectacionTributariaCodigo,
                      afectacionTributariaCodigoTributo: option.codigoTributo,
                      afectacionTributariaImpuesto: option.impuesto !== undefined ? String(option.impuesto) : prev.afectacionTributariaImpuesto,
                      afectacionTributariaNombre: option.nombre,
                    }))}
                  />
                  {showAvailability && (
                    <ProductAvailabilitySection
                      formData={formData}
                      setFormData={setFormData}
                      Establecimientos={Establecimientos}
                      onFieldTouched={() => markTouched('establecimiento')}
                    />
                  )}
                </div>

                {/* 1. Familia de unidades (filtra el select de Unidad mínima) */}
                <div data-tour="producto-unidad">
                  <ProductUnitFamilyField
                    selectedUnitFamily={selectedUnitFamily}
                    isUsingFallbackUnits={isUsingFallbackUnits}
                    handleUnitFamilyChange={handleUnitFamilyChange}
                    unitFamilies={unitFamilies}
                  />
                </div>

                {/* 2. Unidad mínima (filtrada por familia) + Categoría */}
                <div data-tour="producto-clasificacion" className={`grid grid-cols-1 gap-6 ${showCategory ? 'lg:grid-cols-2' : ''}`}>
                  <ProductMinimumUnitField
                    formData={formData}
                    baseUnitOptions={baseUnitOptions}
                    handleBaseUnitChange={handleBaseUnitChange}
                    onBlur={() => markTouched('unidad')}
                  />
                  <ProductCategoryField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    categories={effectiveCategories}
                    onOpenCategoryModal={() => setShowCategoryModal(true)}
                    onBlur={() => markTouched('categoria')}
                  />
                </div>

                <SeccionInformacionTributariaProducto
                  formData={formData}
                  setFormData={setFormData}
                  productType={productType}
                  impuesto={formData.impuesto}
                />

                {/* Alias + Código SUNAT (misma fila) */}
                {(!showAlias || !showSunat) && <div className={`grid grid-cols-1 gap-6 ${showAlias && showSunat ? 'lg:grid-cols-2' : ''}`}>
                  <ProductAliasField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('alias')}
                  />
                  <ProductSunatCodeField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('codigoSunat')}
                  />
                </div>}
              </div>

              {/* Imagen a la derecha */}
              <div data-tour="producto-imagen" className="w-full lg:w-[220px] lg:flex-shrink-0 lg:ml-auto">
                <ProductImageUpload
                  imagePreview={imagePreview}
                  onUpload={handleImageUpload}
                  isFieldVisible={isFieldVisible}
                  isFieldRequired={isFieldRequired}
                  onFilesChange={handleFilesChange}
                  archivosExistentes={archivosExistentes}
                  productoId={product?.id}
                />
              </div>
            </div>
          </div>

            {showPresentacionesComerciales && (
              <ProductAdditionalUnitsTable
                unidadesMedidaAdicionales={formData.unidadesMedidaAdicionales}
                baseUnitCode={formData.unidad}
                additionalUnitErrors={additionalUnitErrors}
                addAdditionalUnit={addAdditionalUnit}
                removeAdditionalUnit={removeAdditionalUnit}
                updateAdditionalUnit={updateAdditionalUnit}
                getAdditionalUnitOptions={getAdditionalUnitOptions}
                remainingUnitsForAdditional={remainingUnitsForAdditional}
                findUnitByCode={findUnitByCode}
                formatFactorValue={formatFactorValue}
                unitInfoMessage={unitInfoMessage}
                setUnitInfoMessage={setUnitInfoMessage}
              />
            )}

            {/* B) CAMPOS COMPLEMENTARIOS */}
            {['descripcion', 'peso', 'marca', 'modelo', 'codigoFabrica', 'tipoExistencia', 'descuentoProducto', 'porcentajeGanancia', 'precioCompra', 'familia', 'centroCosto', 'presupuesto'].some(isFieldVisible) && (
            <div className="space-y-4">
              {/* 9. Descripción (full) */}
              <ProductDescriptionField
                formData={formData}
                setFormData={setFormData}
                errors={visibleErrors}
                isFieldVisible={isFieldVisible}
                isFieldRequired={isFieldRequired}
                isDescriptionExpanded={isDescriptionExpanded}
                onToggleDescription={() => setIsDescriptionExpanded(prev => !prev)}
                onBlur={() => markTouched('descripcion')}
              />

              {/* 11. Fila compacta: Peso + Marca + Modelo + Código fábrica */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-3">
                  <ProductWeightField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('peso')}
                  />
                </div>
                <div className="md:col-span-3">
                  <ProductBrandField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('marca')}
                  />
                </div>
                <div className="md:col-span-3">
                  <ProductModelField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('modelo')}
                  />
                </div>
                <div className="md:col-span-3">
                  <ProductFactoryCodeField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('codigoFabrica')}
                  />
                </div>
              </div>

              {/* 12. Fila compacta 4: Tipo existencia + Descuento + % Ganancia + Precio compra */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-3">
                  <ProductExistenceTypeField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('tipoExistencia')}
                  />
                </div>
                <div className="md:col-span-3">
                  <ProductDiscountField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('descuentoProducto')}
                  />
                </div>
                <div className="md:col-span-3">
                  <ProductProfitPercentField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('porcentajeGanancia')}
                  />
                </div>
                <div className="md:col-span-3">
                  <ProductPurchasePriceField
                    formData={formData}
                    setFormData={setFormData}
                    errors={visibleErrors}
                    isFieldVisible={isFieldVisible}
                    isFieldRequired={isFieldRequired}
                    onBlur={() => markTouched('precioCompra')}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Cuenta contable (opcional)</label>
                  <TextButton
                    id="producto-crear-contable"
                    name="productoCrearContable"
                    onClick={() => setMostrarModalContable(true)}
                    intent="primary"
                    size="sm"
                    type="button"
                  >
                    Crear cuenta contable
                  </TextButton>
                </div>

                {cuentasContables.length ? (
                  <Select
                    id="producto-cuenta-contable"
                    name="productoCuentaContable"
                    value={formData.cuentaContableId ?? ''}
                    onChange={(e) => {
                      const selectedId = e.target.value || null;
                      const selectedAccount = cuentasContables.find((acc) => acc.id === selectedId);
                      setFormData(prev => ({
                        ...prev,
                        cuentaContableId: selectedId,
                        cuentaContableCodigo: selectedAccount?.code ?? null
                      }));
                    }}
                    options={[
                      { value: '', label: 'Sin cuenta contable' },
                      ...cuentasContables.map((account) => ({ value: account.id, label: account.code }))
                    ]}
                    size="medium"
                    helperText="Vincular con cuenta del plan contable"
                  />
                ) : (
                  <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                    <span>Aún no tienes cuentas contables.</span>
                    <TextButton
                      id="producto-cuenta-contable-crear"
                      name="productoCuentaContableCrear"
                      onClick={() => setMostrarModalContable(true)}
                      intent="primary"
                      size="sm"
                      type="button"
                    >
                      Crear
                    </TextButton>
                  </div>
                )}
              </div>

              {showFamilia && (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Familia</label>
                    <TextButton
                      id="producto-crear-familia"
                      name="productoCrearFamilia"
                      onClick={() => setMostrarModalFamilia(true)}
                      intent="primary"
                      size="sm"
                      type="button"
                    >
                      Crear familia
                    </TextButton>
                  </div>

                  {familias.length ? (
                    <Select
                      id="producto-familia"
                      name="productoFamilia"
                      value={formData.familiaId ?? ''}
                      onChange={(e) => {
                        const selectedId = e.target.value || null;
                        const selected = familias.find((f) => f.id === selectedId);
                        setFormData(prev => ({
                          ...prev,
                          familiaId: selectedId,
                          familiaNombre: selected?.nombre ?? null
                        }));
                      }}
                      options={[
                        { value: '', label: 'Sin familia' },
                        ...familias.map((f) => ({ value: f.id, label: `${f.codigo} - ${f.nombre}` }))
                      ]}
                      size="medium"
                    />
                  ) : (
                    <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                      <span>Aún no tienes familias.</span>
                      <TextButton
                        id="producto-familia-crear"
                        name="productoFamiliaCrear"
                        onClick={() => setMostrarModalFamilia(true)}
                        intent="primary"
                        size="sm"
                        type="button"
                      >
                        Crear
                      </TextButton>
                    </div>
                  )}
                </div>
              )}

              {showCentroCosto && (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Centro de costo</label>
                    <TextButton
                      id="producto-crear-centro-costo"
                      name="productoCrearCentroCosto"
                      onClick={() => setMostrarModalCentroCosto(true)}
                      intent="primary"
                      size="sm"
                      type="button"
                    >
                      Crear centro de costo
                    </TextButton>
                  </div>

                  {centrosCosto.length ? (
                    <Select
                      id="producto-centro-costo"
                      name="productoCentroCosto"
                      value={formData.centroCostoId ?? ''}
                      onChange={(e) => {
                        const selectedId = e.target.value || null;
                        const selected = centrosCosto.find((c) => c.id === selectedId);
                        setFormData(prev => ({
                          ...prev,
                          centroCostoId: selectedId,
                          centroCostoNombre: selected?.nombre ?? null
                        }));
                      }}
                      options={[
                        { value: '', label: 'Sin centro de costo' },
                        ...centrosCosto.map((c) => ({ value: c.id, label: `${c.codigo} - ${c.nombre}` }))
                      ]}
                      size="medium"
                    />
                  ) : (
                    <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                      <span>Aún no tienes centros de costo.</span>
                      <TextButton
                        id="producto-centro-costo-crear"
                        name="productoCentroCostoCrear"
                        onClick={() => setMostrarModalCentroCosto(true)}
                        intent="primary"
                        size="sm"
                        type="button"
                      >
                        Crear
                      </TextButton>
                    </div>
                  )}
                </div>
              )}

              {showPresupuesto && (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Presupuesto</label>
                    <TextButton
                      id="producto-crear-presupuesto"
                      name="productoCrearPresupuesto"
                      onClick={() => setMostrarModalPresupuesto(true)}
                      intent="primary"
                      size="sm"
                      type="button"
                    >
                      Crear presupuesto
                    </TextButton>
                  </div>

                  {presupuestos.length ? (
                    <Select
                      id="producto-presupuesto"
                      name="productoPresupuesto"
                      value={formData.presupuestoId ?? ''}
                      onChange={(e) => {
                        const selectedId = e.target.value || null;
                        const selected = presupuestos.find((p) => p.id === selectedId);
                        setFormData(prev => ({
                          ...prev,
                          presupuestoId: selectedId,
                          presupuestoNombre: selected?.nombre ?? null
                        }));
                      }}
                      options={[
                        { value: '', label: 'Sin presupuesto' },
                        ...presupuestos.map((p) => ({ value: p.id, label: `${p.codigo} - ${p.nombre}` }))
                      ]}
                      size="medium"
                    />
                  ) : (
                    <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                      <span>Aún no tienes presupuestos.</span>
                      <TextButton
                        id="producto-presupuesto-crear"
                        name="productoPresupuestoCrear"
                        onClick={() => setMostrarModalPresupuesto(true)}
                        intent="primary"
                        size="sm"
                        type="button"
                      >
                        Crear
                      </TextButton>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}
      </ModalForm>

      <FieldsConfigPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        fieldsConfig={fieldsConfig}
        onApply={applyFieldsConfig}
        onReset={resetToDefault}
      />

      <CategoriaModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onCreated={(categoria) => {
          setLocalCategorias(prev => [...prev, categoria]);
          configDispatch({ type: 'SET_CATEGORIES', payload: [...configState.categories, categoria] });
          setFormData(prev => ({ ...prev, categoria: categoria.nombre }));
          setShowCategoryModal(false);
        }}
      />

      <AccountingAccountModal
        isOpen={mostrarModalContable}
        mode="create"
        onClose={() => setMostrarModalContable(false)}
        onSubmit={onSubmitGuardarModalContable}
      />

      <ModalCatalogoCodigoNombre
        isOpen={mostrarModalFamilia}
        title="Agregar familia"
        codigoLabel="Código de familia"
        nombreLabel="Nombre de familia"
        onClose={() => setMostrarModalFamilia(false)}
        onSubmit={onSubmitGuardarModalFamilia}
      />

      <ModalCatalogoCodigoNombre
        isOpen={mostrarModalCentroCosto}
        title="Agregar centro de costo"
        codigoLabel="Código de centro de costo"
        nombreLabel="Nombre de centro de costo"
        onClose={() => setMostrarModalCentroCosto(false)}
        onSubmit={onSubmitGuardarModalCentroCosto}
      />

      <ModalCatalogoCodigoNombre
        isOpen={mostrarModalPresupuesto}
        title="Agregar presupuesto"
        codigoLabel="Código de presupuesto"
        nombreLabel="Nombre de presupuesto"
        onClose={() => setMostrarModalPresupuesto(false)}
        onSubmit={onSubmitGuardarModalPresupuesto}
      />
    </>
  );
};

export default ProductModal;


